import { LangMap } from './types';

/**
 * Kravnøkkelen som knytter et vedlegg til ett krav i kravtreet.
 *
 * Nøkkelen settes på `simulatorResultItemUri` — IKKE på `group`. `group` er en
 * backend-generert Guid som er ruteparameter, fremmednøkkel fra fartstidsrapporter
 * og har en «samme AttachmentType per gruppe»-skranke.
 */
export const CERTREQ_URI_PREFIX = 'certreq';

/**
 * Bygger kravnøkkelen `certreq:{certificateCode}#{requirementId}`, f.eks.
 * `certreq:D1#PS_Course_MBH`. Mangler koden eller kravets id kan vi ikke lage en
 * stabil nøkkel, og returnerer null — da skal det ikke tilbys opplasting.
 */
export const buildRequirementKey = (
  certificateCode?: string | null,
  requirementId?: string | null
): string | null => {
  if (!certificateCode || !requirementId) return null;
  return `${CERTREQ_URI_PREFIX}:${certificateCode}#${requirementId}`;
};

/**
 * Visningskopi som skrives i vedleggets `metadata` (fri JSON-streng).
 *
 * `imageEditOptions` er med som valgfritt felt fordi APS' egen AttachmentMetadata
 * kun har det feltet. Uten et felles felt ville TypeScript avvise objektet
 * («weak type»). Feltet settes aldri, og JSON.stringify utelater det.
 */
export type CertReqAttachmentMetadata = {
  imageEditOptions?: null;
  certreq: {
    requirementId: string;
    certificateCode: string;
    labelNo?: string;
    labelEn?: string;
    /**
     * Kravtypen slik den står i kravdokumentet («Course», «SeagoingService»,
     * «ProfilePicture»). Med den er vedlegget selvbeskrivende: saksbehandleren
     * ser hva slags dokumentasjon fila er uten å slå opp i kravtreet.
     *
     * Valgfritt — en gruppe uten entydig kravtype sender den ikke, og vedlegg
     * lastet opp før feltet fantes har den ikke.
     */
    requirementType?: string;
  };
};

export type BuildRequirementMetadataParams = {
  certificateCode: string;
  requirementId: string;
  label?: LangMap;
  requirementType?: string;
};

/** Lager metadata-objektet som gjør saksbehandlervisningen (PR-6) rent frontend-arbeid. */
export const buildRequirementMetadata = ({
  certificateCode,
  requirementId,
  label,
  requirementType,
}: BuildRequirementMetadataParams): CertReqAttachmentMetadata => ({
  certreq: {
    requirementId,
    certificateCode,
    labelNo: label?.no || label?.nb,
    labelEn: label?.en,
    requirementType,
  },
});
